import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { supabase } from '../lib/supabase';

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add Supabase auth token
apiClient.interceptors.request.use(
  async (config) => {
    // Get current Supabase session
    const { data: { session } } = await supabase.auth.getSession();
    
    if (session?.access_token) {
      config.headers.Authorization = `Bearer ${session.access_token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Try to refresh the Supabase session
        const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();
        
        if (refreshError || !session) {
          // Refresh failed, redirect to login
          window.location.href = '/auth/login';
          return Promise.reject(error);
        }

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${session.access_token}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed, redirect to login
        window.location.href = '/auth/login';
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  getProfile: async () => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },
  
  updateProfile: async (data: any) => {
    const response = await apiClient.patch('/auth/profile', data);
    return response.data;
  },
  
  changePassword: async (currentPassword: string, newPassword: string) => {
    const response = await apiClient.post('/auth/change-password', {
      currentPassword,
      newPassword
    });
    return response.data;
  },
  
  refreshToken: async (refreshToken: string) => {
    const response = await apiClient.post('/auth/refresh', { refreshToken });
    return response.data;
  }
};

// Projects API
export const projectsApi = {
  getProjects: async () => {
    const response = await apiClient.get('/projects');
    return response.data;
  },

  getProject: async (projectId: string) => {
    const response = await apiClient.get(`/projects/${projectId}`);
    return response.data;
  },

  createProject: async (data: any) => {
    const response = await apiClient.post('/projects', data);
    return response.data;
  },

  updateProject: async (projectId: string, data: any) => {
    const response = await apiClient.patch(`/projects/${projectId}`, data);
    return response.data;
  },

  deleteProject: async (projectId: string) => {
    const response = await apiClient.delete(`/projects/${projectId}`);
    return response.data;
  },
  
  setupOnboardingProject: async (data: any) => {
    const response = await apiClient.patch('/projects/onboarding/setup', data);
    return response.data;
  },

  getProjectConnections: async (projectId: string) => {
    const response = await apiClient.get(`/projects/${projectId}/connections`);
    return response.data;
  }
};

// Agents API
export const agentsApi = {
  getAgents: async () => {
    const response = await apiClient.get('/agents');
    return response.data;
  },

  getAgent: async (agentType: string) => {
    const response = await apiClient.get(`/agents/${agentType}`);
    return response.data;
  },

  chat: async (agentType: string, data: {
    message: string;
    conversationId?: string;
    context?: Record<string, any>;
    projectId?: string;
  }) => {
    const response = await apiClient.post(`/agents/${agentType}/chat`, data);
    return response.data;
  },

  getConversations: async (params?: {
    projectId?: string;
    agentType?: string;
    limit?: number;
    offset?: number;
  }) => {
    const response = await apiClient.get('/agents/conversations', { params });
    return response.data;
  },

  getConversation: async (conversationId: string, params?: {
    limit?: number;
    offset?: number;
  }) => {
    const response = await apiClient.get(`/agents/conversations/${conversationId}`, { params });
    return response.data;
  },

  archiveConversation: async (conversationId: string) => {
    const response = await apiClient.patch(`/agents/conversations/${conversationId}/archive`);
    return response.data;
  },

  deleteConversation: async (conversationId: string) => {
    const response = await apiClient.delete(`/agents/conversations/${conversationId}`);
    return response.data;
  },

  getUsageStats: async (params?: { period?: string }) => {
    const response = await apiClient.get('/agents/usage/stats', { params });
    return response.data;
  },
};

// Analytics API
export const analyticsApi = {
  getOverview: async (projectId: string, params?: {
    startDate?: string;
    endDate?: string;
  }) => {
    const response = await apiClient.get(`/analytics/projects/${projectId}/overview`, { params });
    return response.data;
  },

  getRealtime: async (projectId: string) => {
    const response = await apiClient.get(`/analytics/projects/${projectId}/realtime`);
    return response.data;
  },

  getTraffic: async (projectId: string, params?: {
    startDate?: string;
    endDate?: string;
  }) => {
    const response = await apiClient.get(`/analytics/projects/${projectId}/traffic`, { params });
    return response.data;
  },

  getAudience: async (projectId: string, params?: {
    startDate?: string;
    endDate?: string;
  }) => {
    const response = await apiClient.get(`/analytics/projects/${projectId}/audience`, { params });
    return response.data;
  },

  getConversions: async (projectId: string, params?: {
    startDate?: string;
    endDate?: string;
  }) => {
    const response = await apiClient.get(`/analytics/projects/${projectId}/conversions`, { params });
    return response.data;
  },

  // New GA4 Audience Export methods
  listAudiences: async (projectId: string) => {
    const response = await apiClient.get(`/analytics/projects/${projectId}/audiences/list`);
    return response.data;
  },

  createAudienceExport: async (projectId: string, audienceId: string, dimensions: string[] = ['deviceId']) => {
    const response = await apiClient.post(`/analytics/projects/${projectId}/audiences/${audienceId}/export`, {
      dimensions
    });
    return response.data;
  },

  checkAudienceExportStatus: async (projectId: string, exportId: string) => {
    const response = await apiClient.get(`/analytics/projects/${projectId}/audience-exports/${exportId}/status`);
    return response.data;
  },

  queryAudienceExportData: async (projectId: string, exportId: string, audienceId?: string) => {
    const params = audienceId ? { audienceId } : {};
    const response = await apiClient.get(`/analytics/projects/${projectId}/audience-exports/${exportId}/data`, { params });
    return response.data;
  },

  queryGA4: async (projectId: string, query: any) => {
    const response = await apiClient.post(`/analytics/projects/${projectId}/ga4/query`, query);
    return response.data;
  },

  getGoalOptions: async (projectId: string) => {
    const response = await apiClient.get(`/analytics/projects/${projectId}/goal-options`);
    return response.data;
  },

  getDashboardInsights: async (projectId: string) => {
    const response = await apiClient.get(`/analytics/projects/${projectId}/dashboard/insights`);
    return response.data;
  }
};

// Billing API
export const billingApi = {
  getSubscription: async () => {
    const response = await apiClient.get('/billing/subscription');
    return response.data;
  },

  getUsage: async (params?: {
    startDate?: string;
    endDate?: string;
  }) => {
    const response = await apiClient.get('/billing/usage', { params });
    return response.data;
  },

  createCheckoutSession: async (data: {
    productId: string;
    successUrl?: string;
    cancelUrl?: string;
  }) => {
    const response = await apiClient.post('/billing/checkout', data);
    return response.data;
  },

  createPortalSession: async (data?: {
    returnUrl?: string;
  }) => {
    const response = await apiClient.post('/billing/portal', data);
    return response.data;
  },

  syncSubscription: async () => {
    const response = await apiClient.post('/billing/sync');
    return response.data;
  },

  getInvoices: async (params?: {
    limit?: number;
    startingAfter?: string;
  }) => {
    const response = await apiClient.get('/billing/invoices', { params });
    return response.data;
  },

  downloadInvoice: async (invoiceId: string) => {
    const response = await apiClient.get(`/billing/invoices/${invoiceId}/download`);
    return response.data;
  }
};

// Products API
export const productsApi = {
  getProducts: async () => {
    const response = await apiClient.get('/products');
    return response.data;
  },

  getProduct: async (productId: string) => {
    const response = await apiClient.get(`/products/${productId}`);
    return response.data;
  }
};

// Reports API
export const reportsApi = {
  getReports: async (projectId?: string) => {
    const response = await apiClient.get('/reports', { params: { projectId } });
    return response.data;
  },

  getReport: async (reportId: string) => {
    const response = await apiClient.get(`/reports/${reportId}`);
    return response.data;
  },

  createReport: async (data: any) => {
    const response = await apiClient.post('/reports', data);
    return response.data;
  },

  updateReport: async (reportId: string, data: any) => {
    const response = await apiClient.patch(`/reports/${reportId}`, data);
    return response.data;
  },

  deleteReport: async (reportId: string) => {
    const response = await apiClient.delete(`/reports/${reportId}`);
    return response.data;
  },

  generateReport: async (reportId: string) => {
    const response = await apiClient.post(`/reports/${reportId}/generate`);
    return response.data;
  },

  exportReport: async (reportId: string, format: string) => {
    const response = await apiClient.get(`/reports/${reportId}/export`, { 
      params: { format },
      responseType: 'blob'
    });
    return response.data;
  },

  getTemplates: async () => {
    const response = await apiClient.get('/reports/templates');
    return response.data;
  },

  getScheduledReports: async (projectId?: string) => {
    const response = await apiClient.get('/reports/scheduled', { params: { projectId } });
    return response.data;
  }
};

// Organizations API
export const organizationsApi = {
  getOrganization: async (organizationId: string) => {
    const response = await apiClient.get(`/organizations/${organizationId}`);
    return response.data;
  },

  getMembers: async (organizationId: string) => {
    const response = await apiClient.get(`/organizations/${organizationId}/members`);
    return response.data;
  },

  updateOrganization: async (organizationId: string, data: any) => {
    const response = await apiClient.patch(`/organizations/${organizationId}`, data);
    return response.data;
  },

  inviteMember: async (organizationId: string, data: any) => {
    const response = await apiClient.post(`/organizations/${organizationId}/members`, data);
    return response.data;
  },

  updateMember: async (organizationId: string, memberId: string, data: any) => {
    const response = await apiClient.patch(`/organizations/${organizationId}/members/${memberId}`, data);
    return response.data;
  },

  removeMember: async (organizationId: string, memberId: string) => {
    const response = await apiClient.delete(`/organizations/${organizationId}/members/${memberId}`);
    return response.data;
  }
};

export default apiClient;